import React, { useState, useEffect, useRef } from 'react';
import axios from '../../utilities/axios';
import * as API_END_POINT from '../../utilities/constant';
// import 'https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4';
import { useStore } from '../../context/StoreContext';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { escapeHtml } from '../../utilities/helper';
import buildBannerHtml, { accentTextOf, copyOf, DEFAULT_THEME, parseVideoEmbed } from './bannerMarkup';
import { attachVideoControls } from './videoControls';
import { readableOn } from '../../shared/theme/storefrontTheme';
import { ShareMeta } from '../../shared/components';
import buildBrandsHtml from './brandsMarkup';
import buildProductsHtml, { normalizeProduct } from './productsMarkup';
import buildCollectionsHtml, { normalizeCollection, normalizeCollectionIds } from './collectionsMarkup';
import buildSpotlightHtml, { SPOTLIGHT_PRODUCT_LIMIT } from './spotlightMarkup';
import { SKELETON_STYLES } from './skeletonStyles';
import { applyCategoryTileImages } from './categoryTileImages';

/* Pinned ends are a product rule, not a property of the saved order: only the
   middle group is draggable in the designer, so the renderer restates the
   grouping rather than trusting a componentOrder that may have drifted. */
const PINNED_TOP_TYPES = ['announcementBar', 'navigation', 'mainBanner'];
const PINNED_BOTTOM_TYPES = ['footer'];

const normalizeComponentOrder = (componentOrder) => {
  const blocks = (componentOrder || []).filter(Boolean);
  const isPinned = (type) => PINNED_TOP_TYPES.includes(type) || PINNED_BOTTOM_TYPES.includes(type);
  // Pinned group only — a stale copy left in the draggable range would render a
  // second banner or footer further down the page.
  const pinnedBlock = (type) => blocks.find((block) => block.type === type);

  return [
    ...PINNED_TOP_TYPES.map(pinnedBlock).filter(Boolean),
    ...blocks.filter((block) => !isPinned(block.type)),
    ...PINNED_BOTTOM_TYPES.map(pinnedBlock).filter(Boolean),
  ];
};

/* Featured categories, Meet the team, Customer testimonials, Quote over a
   photo, Questions & answers and Closing message. Their published
   markup is final (the designer builds it, styles included), so the storefront
   only drops an empty or missing one. */
const CONTENT_CONTAINER_PREFIX = {
  featuredCategories: 'featured-category',
  meetTeams: 'meet-team',
  testimonials: 'testimonials',
  statements: 'statement',
  faqs: 'faq',
  closings: 'closing',
};

/* The page wrapper's 15px mobile gutter would inset these full-width bands. */
const CONTENT_SECTION_STYLES = `
  @media screen and (max-width: 767px) {
    ${Object.values(CONTENT_CONTAINER_PREFIX).map((prefix) => `section[id^="${prefix}-container-"]`).join(',\n    ')} { margin-inline: -15px; }
  }
`;

/* Container id prefix per hideable section type. Navigation, the Main banner
   and the Footer have no on/off state at all; the announcement bar carries
   announcementBarEnabled instead and is stripped by functionInitialAction. */
const HIDEABLE_CONTAINER_PREFIX = {
  textBlocks: 'text-block',
  imageWithTextBlocks: 'image-with-text',
  featuredProducts: 'featured-product',
  featuredCollections: 'featured-collection',
  collectionSpotlights: 'collection-spotlight',
  featuredBrands: 'featured-brand',
  banners: 'banner',
  linkLists: 'link-list',
  promos: 'promo',
  ...CONTENT_CONTAINER_PREFIX,
};

/* Fallback heading for a Text block, so a cleared field still reads as a
   section instead of an empty <h2>. */
const TEXT_BLOCK_SEED_HEADING = 'A note from us';

/* Text block layout. A storefront published before this redesign carries none
   of these rules in its html_content, so the page states them itself. */
const TEXT_BLOCK_STYLES = `
  /* Zero the editor's 15px container gutter so .sf-textblock owns the same
     40/20 edge as the reference, instead of the two stacking. */
  section[id^="text-block-container-"] { padding-inline: 0 !important; }
  /* Below 768px the page wrapper (storebuilder-reset) still carries its own
     15px side gutter (px-[15px]); zeroing this section's own padding above
     does not cancel that — the wrapper's padding pushes every child inward
     regardless. Breaking out with a negative margin keeps the section itself
     full-bleed, so only .sf-textblock's own inset governs, same fix as
     #main-banner-container. */
  @media screen and (max-width: 767px) {
    section[id^="text-block-container-"] { margin-inline: -15px; }
  }
  .sf-textblock-sec { padding: 0 0 32px; }
  .sf-textblock {
    max-width: 660px;
    margin: 0 auto;
    padding: 0 40px;
    text-align: center;
  }
  .sf-textblock h2 {
    font-size: clamp(21px, 2.4vw, 27px);
    line-height: 1.25;
    letter-spacing: -0.01em;
    color: #2C2F32;
    margin: 0 0 12px;
  }
  .sf-textblock p {
    color: #565E64;
    font-size: 15.5px;
    line-height: 1.7;
    margin: 0;
    white-space: pre-line;
  }

  @media (max-width: 860px) {
    .sf-textblock-sec { padding: 0 0 28px; }
    .sf-textblock { padding: 0 20px; }
  }
`;

/* What an Image with text block falls back to when its heading is left blank. */
const IMAGE_WITH_TEXT_SEED_HEADING = 'Why we do this';

/* Image with text — a photo beside a paragraph from tablet up, stacked below.
   Stated here for the same reason as the Text block above. */
const IMAGE_WITH_TEXT_STYLES = `
  section[id^="image-with-text-container-"] { padding-inline: 0 !important; }
  /* Same wrapper-gutter breakout as the Text block above: without it this
     section sits inset by the page wrapper's 15px on mobile only, on top of
     its own 20px, reading as a floating card instead of the reference's
     edge-to-edge section with an even inner gutter at every width. */
  @media screen and (max-width: 767px) {
    section[id^="image-with-text-container-"] { margin-inline: -15px; }
  }
  .sf-imgtext-sec { padding: 0 0 32px; }
  .sf-imgtext {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 36px;
    align-items: center;
    padding: 0 40px;
  }
  .sf-imgtext.is-right .it-img { order: 2; }
  /* Without a photo the copy would sit in half the page beside an empty column. */
  .sf-imgtext.is-copy-only { grid-template-columns: 1fr; }
  /* The photo at its own ratio, as on the main branch — no fixed frame, so
     nothing is cropped. */
  .it-img {
    position: relative;
    border-radius: 4px;
    overflow: hidden;
  }
  .it-img img {
    width: 100%;
    height: auto;
    display: block;
  }
  .it-copy h2 {
    font-size: clamp(21px, 2.4vw, 27px);
    line-height: 1.25;
    letter-spacing: -0.01em;
    color: #2C2F32;
    margin: 0 0 12px;
  }
  .it-copy p {
    color: #565E64;
    font-size: 15.5px;
    line-height: 1.65;
    margin: 0 0 18px;
    white-space: pre-line;
  }
  .it-copy p:last-child { margin-bottom: 0; }
  .it-btn {
    display: inline-flex;
    align-items: center;
    border: 1px solid #CDD1D4;
    border-radius: 8px;
    padding: 10px 18px;
    background: #FFFFFF;
    font-family: inherit;
    font-size: 14.5px;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
    text-decoration: none;
    transition: background-color .14s, box-shadow .14s;
  }
  /* The reference darkens the border on hover, but border and text are written
     inline from the store's color and inline wins — so the fill is the only
     property left free. */
  .it-btn:hover,
  .it-btn:focus-visible {
    background: #F5F6F6;
    box-shadow: 0 1px 2px 0 rgba(44,47,50,0.06);
  }
  .it-btn:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 2px; }

  @media (max-width: 860px) {
    .sf-imgtext-sec { padding: 0 0 28px; }
    .sf-imgtext {
      grid-template-columns: 1fr;
      gap: 22px;
      padding: 0 20px;
    }
    .sf-imgtext.is-right .it-img { order: 0; }
  }
`;

/* Banner layout. Matches SP-main: the hero is a full-bleed, square-cornered band
   at every width, never a card inset inside the section gutter. */
const BANNER_STYLES = `
  /* Button colors are inline per surface (see surfaceOf() in bannerMarkup) and
     outrank anything stated here, so the hover is a brightness filter rather
     than a color swap — the same fallback the reference uses. */
  .hero-btn-primary,
  .hero-btn-secondary { transition: filter .14s, box-shadow .14s; }
  .hero-btn-primary:hover,
  .hero-btn-secondary:hover,
  .hero-btn-primary:focus-visible,
  .hero-btn-secondary:focus-visible { filter: brightness(0.92); }
  /* A white ring inside a dark halo: these buttons sit on a photo, on the
     store's color or on a 10% tint of it, and any single flat ring color is
     invisible on one of the three. */
  .hero-btn-primary:focus-visible,
  .hero-btn-secondary:focus-visible {
    outline: 2px solid #FFFFFF;
    outline-offset: 2px;
    box-shadow: 0 0 0 5px rgba(20,17,15,0.45);
  }

  /* Zero the banner's own padding at every width — redundant with the parent's
     md:px-[0] at >=768px, but load-bearing below it (see the margin-inline rule
     next, which needs the banner to have no inset of its own to break out of). */
  #main-banner-container.custom-storebuilder-padding,
  #main-banner-container,
  section[id^="banner-container-"] {
    padding-inline: 0 !important;
    padding-top: 0 !important;
  }

  /* An added banner's section is a grid (its classes come from the saved page
     HTML), and a grid's implicit column grows to its content: one long
     unbroken headline (w-max) widened the column — and the page — past the
     1440px container before the headline's max-w-full could bite. A 0-minimum
     column holds it to the section's width. */
  section[id^="banner-container-"] { grid-template-columns: minmax(0, 1fr); }

  /* Below 768px the page wrapper (storebuilder-reset) still carries its own
     15px side gutter (px-[15px]), which every other section sits inside. The
     banner instead breaks out of it with a negative margin, so it stays
     full-bleed and square-cornered on mobile too, matching SP-main. */
  @media screen and (max-width: 767px) {
    #main-banner-container.custom-storebuilder-padding,
    #main-banner-container,
    section[id^="banner-container-"] {
      margin-inline: -15px;
    }
  }
`;

/* Link list — SP-main's `.sf-linklist`: a narrow column of labelled rows with a
   trailing arrow, separated by hairlines. No icons and no new-tab flag. */
const LINK_LIST_SEED_HEADING = 'More from us';
const LINK_LIST_STYLES = `
  section[id^="link-list-container-"] { padding-inline: 0 !important; }
  /* Same wrapper-gutter breakout as the Text block, see above. */
  @media screen and (max-width: 767px) {
    section[id^="link-list-container-"] { margin-inline: -15px; }
  }
  .sf-linklist-sec { padding: 0 0 32px; }
  .sf-linklist { max-width: 560px; padding: 0 40px; box-sizing: content-box; }
  .sf-linklist h2 {
    font-size: clamp(20px, 2.2vw, 25px);
    line-height: 1.25;
    letter-spacing: -0.01em;
    color: #2C2F32;
    margin: 0 0 14px;
  }
  .sf-linklist ul { list-style: none; margin: 0; padding: 0; }
  .sf-linklist li { border-bottom: 1px solid #E5E7E8; }
  .sf-linklist a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 2px;
    font-size: 15.5px;
    color: #565E64;
    text-decoration: none;
    transition: color .14s;
  }
  /* Near-black, not the storefront orange — orange is reserved for buttons.
     Scoped through the section rather than as a bare selector: html_content's
     own sheet is injected into the body after the Helmet, so its
     '.sf-linklist a:hover' (the resting color) would win on source order. */
  .sf-linklist-sec .sf-linklist a:hover,
  .sf-linklist-sec .sf-linklist a:focus-visible { color: #2C2F32; }
  /* Inset: an outset ring would sit outside the section's own 40px edge. */
  .sf-linklist-sec .sf-linklist a:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: -2px; border-radius: 4px; }
  .sf-linklist a svg { width: 16px; height: 16px; flex: none; }

  @media (max-width: 860px) {
    .sf-linklist-sec { padding: 0 0 28px; }
    .sf-linklist { padding: 0 20px; }
  }
`;

/* Seeds from SP-main's `storefrontSeedText('promo')`, the same strings the
   designer shows as placeholders, so a cleared field still reads as an offer. */
const PROMO_SEED_TITLE = 'A little something for new customers.';
const PROMO_SEED_SUBTITLE = 'A small thank-you for shopping with us.';

/* Special offer — SP-main's `.sf-promo`: a full-bleed band in the store's color,
   copy left and one white button right, so the container gutter is zeroed and
   the inner row carries the 40/20 edge. The band color is not configurable:
   four free color pickers on the one strip meant to be noticed is how a
   storefront ends up with an unreadable pair. The Style tab owns the palette. */
const PROMO_STYLES = `
  section[id^="promo-container-"] { padding-inline: 0 !important; }
  /* Same wrapper-gutter breakout as the Text block, see above — otherwise the
     color band shows the page background down both sides on mobile only. */
  @media screen and (max-width: 767px) {
    section[id^="promo-container-"] { margin-inline: -15px; }
  }
  .sf-promo { color: #FFFFFF; padding: 36px 0; }
  .sf-promo-in {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
    padding: 0 40px;
  }
  .sf-promo h3 {
    font-size: 25px;
    line-height: 1.25;
    letter-spacing: -0.01em;
    color: #FFFFFF;
    margin: 0;
  }
  .sf-promo p {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14.5px;
    line-height: 1.5;
    margin: 6px 0 0;
    white-space: pre-line;
  }
  /* White on the color band is the one pairing that stays readable whatever the
     store's color is. */
  .sf-promo .pr-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #FFFFFF;
    color: #2C2F32;
    border: 1px solid transparent;
    border-radius: 10px;
    padding: 12px 20px;
    font-family: inherit;
    font-size: 14.5px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    text-decoration: none;
    transition: background-color .14s, box-shadow .14s;
  }
  /* The reference gives this button no hover, but it is the only thing to click
     on the band, and it cannot darken toward the accent — the band IS the
     accent — so it steps down to the neutral surface tone and lifts. */
  .sf-promo .pr-btn:hover,
  .sf-promo .pr-btn:focus-visible {
    background: #F5F6F6;
    box-shadow: 0 4px 12px 0 rgba(20,17,15,0.22);
  }
  /* White, not orange: the band behind it is the store's color. */
  .sf-promo .pr-btn:focus-visible { outline: 2px solid #FFFFFF; outline-offset: 3px; }

  @media (max-width: 860px) {
    .sf-promo { padding: 28px 0; }
    .sf-promo-in { padding: 0 20px; }
    .sf-promo h3 { font-size: 22px; }
  }
`;

/* The rail shell — section header, "View all", arrows, scroller — shared by
   Featured brands and Featured products; each section's own styles carry only
   its surface and its card. A storefront published before this redesign carries
   none of these rules, so the page states them itself. */
const RAIL_STYLES = `
  /* min-width:0 on all four is load-bearing. The published container is a
     Tailwind grid, and a grid item's default min-width is auto — so a flex rail
     of fixed-width tiles widens its own track instead of scrolling, the PAGE
     gains a horizontal scrollbar, and the arrows go dead because a rail that
     was allowed to grow never reports itself as scrollable. */
  section[id^="featured-brand-container-"],
  section[id^="featured-product-container-"],
  section[id^="featured-collection-container-"],
  section[id^="collection-spotlight-container-"] { padding-inline: 0 !important; min-width: 0; }
  /* Same wrapper-gutter breakout as the Text block above (see
     IMAGE_WITH_TEXT_STYLES) — without it these rails sit inset by the page
     wrapper's 15px on mobile only, on top of their own 20px, so a card at the
     start of the rail reads as floating with rounded corners and background
     peeking on both sides instead of the reference's edge-to-edge section. */
  @media screen and (max-width: 767px) {
    section[id^="featured-brand-container-"],
    section[id^="featured-product-container-"],
    section[id^="featured-collection-container-"],
    section[id^="collection-spotlight-container-"] { margin-inline: -15px; }
  }
  .sf-brands-sec,
  .sf-products-sec,
  .sf-collections-sec { padding: 32px 0; min-width: 0; max-width: 100%; }
  .sf-brands-sec { background: #FAFBFB; }
  .sf-products-sec { background: #FFFFFF; }
  .sf-collections-sec { background: #FFFFFF; }
  .sf-brands-sec .sf-in,
  .sf-products-sec .sf-in,
  .sf-collections-sec .sf-in { padding: 0 40px; width: 100%; min-width: 0; box-sizing: border-box; }

  .sf-sh {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    margin-bottom: 24px;
  }
  .sf-sh h2 {
    font-size: 25px;
    line-height: 1.2;
    letter-spacing: -0.01em;
    color: #2C2F32;
    margin: 0;
  }
  .sf-sh .sub {
    color: #565E64;
    font-size: 14.5px;
    line-height: 1.5;
    margin-top: 5px;
    max-width: 540px;
  }
  /* Color is set inline per section from the Style tab; near-black made the one
     link in the header row read as a heading rather than as a link. */
  .sf-seeall {
    font-size: 13.5px;
    font-weight: 600;
    white-space: nowrap;
    text-decoration: none;
  }
  .sf-seeall { transition: text-decoration-color .14s; }
  .sf-seeall:hover,
  .sf-seeall:focus-visible { text-decoration: underline; }
  .sf-seeall:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 3px; border-radius: 3px; }

  /* The arrows share the header row with "View all", so the header reserves
     room for them rather than letting the two overlap and clip the link. */
  .sf-railwrap { position: relative; min-width: 0; }
  /* Reserved unconditionally rather than tested with :has() — the designer's
     copy of this sheet goes through GrapesJS's CSS parser, and this section
     always has arrows. */
  .sf-brands-sec .sf-sh,
  .sf-products-sec .sf-sh,
  .sf-collections-sec .sf-sh { padding-right: 88px; }
  .sf-arrows { position: absolute; right: 0; top: -54px; display: flex; gap: 8px; }
  .sf-arr {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid #CDD1D4;
    background: #FFFFFF;
    display: grid;
    place-items: center;
    color: #565E64;
    cursor: pointer;
  }
  .sf-arr { transition: border-color .14s, background-color .14s, color .14s; }
  .sf-arr:hover,
  .sf-arr:focus-visible { border-color: #808B90; background: #F5F6F6; color: #2C2F32; }
  .sf-arr:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 2px; }
  .sf-arr svg { width: 15px; height: 15px; }
  .sf-arr[disabled],
  .sf-arr.scroll-disabled,
  .scroll-left.scroll-disabled,
  .scroll-right.scroll-disabled { opacity: 0.35; cursor: default; pointer-events: none; }

  /* Only the rail scrolls, and .sf-in bounds its width, so a long row never
     widens the page. */
  .sf-rail {
    display: flex;
    gap: 16px;
    min-width: 0;
    overflow-x: auto;
    scroll-behavior: smooth;
    scrollbar-width: none;
    scroll-snap-type: x proximity;
    padding-bottom: 4px;
  }
  .sf-rail::-webkit-scrollbar { display: none; }

  @media (max-width: 860px) {
    .sf-brands-sec,
    .sf-products-sec,
    .sf-collections-sec { padding: 28px 0; }
    .sf-brands-sec .sf-in,
    .sf-products-sec .sf-in,
    .sf-collections-sec .sf-in { padding: 0 20px; }
    .sf-sh h2 { font-size: 21px; }
  }

  /* On a phone the arrows leave their absolute slot and sit under the heading:
     holding the 88px gutter squeezed the heading to roughly 180px, wrapping it
     to two lines beside empty space. */
  @media (max-width: 560px) {
    .sf-brands-sec .sf-sh,
    .sf-products-sec .sf-sh,
    .sf-collections-sec .sf-sh {
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
      padding-right: 0;
    }
    .sf-arrows { display: none !important; }
    .sf-seeall { align-self: flex-end; }
  }
`;

/* Featured brands — the tile itself, on the secondary surface, matching
   SP-main's brands section and the shared `.bb-card`. */
const BRANDS_STYLES = `
  .bb-card {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    text-align: center;
    border: 1px solid #E5E7E8;
    border-radius: 4px;
    background: #FFFFFF;
    cursor: pointer;
    padding: 0;
    overflow: hidden;
    text-decoration: none;
    transition: border-color .14s, box-shadow .14s, transform .14s;
  }
  .bb-card:hover {
    border-color: #CDD1D4;
    box-shadow: 0 4px 12px 0 rgba(44,47,50,0.06), 0 2px 4px 0 rgba(44,47,50,0.04);
    transform: translateY(-3px);
  }
  .bb-card:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 2px; }
  .sf-bcard { flex: 0 0 200px; }

  /* Copied from the portal's BrandCard, so one brand looks the same on Browse
     brands, in the designer and here. The 2px ring in the brand's own color is
     what makes a row of mixed logo shapes read as a set; a rectangular logo is
     matted on a white circle, and with no logo the disc fills with that color
     and carries one initial. */
  .bb-logo {
    box-sizing: border-box;
    width: 104px;
    height: 104px;
    border-radius: 50%;
    border: 2px solid #CDD1D4;
    display: grid;
    place-items: center;
    overflow: hidden;
    margin: 18px auto 16px;
    font-size: 28px;
    font-weight: 600;
    color: #FFFFFF;
    background-color: #CDD1D4;
  }
  .bb-logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 6px;
    background: #FFFFFF;
    border-radius: 50%;
  }

  .bb-name {
    font-size: 14px;
    font-weight: 600;
    color: #2C2F32;
    margin: 0;
    letter-spacing: -0.01em;
    border-top: 1px solid #E5E7E8;
    padding: 11px 12px;
  }
`;

/* Featured products — the card itself, on the page's white surface, matching
   SP-main's `.sf-prod`. The reference card carries an "Add to cart" button;
   buying happens on the product page here, so there is no `.sf-add`. */
const PRODUCTS_STYLES = `
  /* Sized from the rail rather than pinned to 210px, so five always fit — four
     16px gaps come out of the 100%; at a fixed width the last card was always a
     sliver. Unlike SP-main's .sf-prod the card takes the shared lift-and-ring
     hover, because here the whole tile is the link and nothing else answers the
     pointer. */
  .sf-prod {
    flex: 0 0 calc((100% - 64px) / 5);
    border: 1px solid #E5E7E8;
    border-radius: 4px;
    overflow: hidden;
    background: #FFFFFF;
    display: flex;
    flex-direction: column;
    text-decoration: none;
    transition: border-color .14s, box-shadow .14s, transform .14s;
  }
  .sf-prod:hover {
    border-color: #CDD1D4;
    box-shadow: 0 4px 12px 0 rgba(44,47,50,0.06), 0 2px 4px 0 rgba(44,47,50,0.04);
    transform: translateY(-2px);
  }
  .sf-prod:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 2px; }

  .sf-pimg { aspect-ratio: 1 / 1; position: relative; overflow: hidden; background: #FAFBFB; }
  .sf-pimg img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; display: block; }
  /* Stand-in for a product with no photo yet — a box glyph, never a broken <img>. */
  .sf-pimg .ph { width: 100%; height: 100%; display: grid; place-items: center; color: #808B90; }
  .sf-pimg .ph svg { width: 34px; height: 34px; opacity: 0.6; }

  .sf-pb { padding: 12px 13px 13px; display: flex; flex-direction: column; flex: 1; }
  .sf-pbrand { font-size: 12px; color: #565E64; }
  .sf-pname { font-size: 14px; font-weight: 500; line-height: 1.3; margin: 3px 0 10px; flex: 1; color: #2C2F32; }
  .sf-pfoot { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  .sf-price { font-weight: 600; font-size: 15px; color: #2C2F32; }

  /* Narrower viewports step down rather than shrink. At phone width the card
     goes back to a fixed 210px, so a partly visible card at the right edge says
     the rail scrolls. */
  @media (max-width: 1024px) {
    .sf-prod { flex: 0 0 calc((100% - 32px) / 3); }
  }
  @media (max-width: 860px) {
    .sf-prod { flex: 0 0 210px; }
  }
`;

/* Featured collections — the cover card itself, matching SP-main's shared
   `.ccard`. The portal's version carries a Share button and a product count;
   the storefront's does not, so there are no `.cshare` / `.pc` rules. */
const COLLECTIONS_STYLES = `
  .sf-rail > .ccard { flex: 0 0 230px; }
  .ccard {
    background: #FFFFFF;
    border: 1px solid #E5E7E8;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    text-decoration: none;
    display: flex;
    flex-direction: column;
    transition: border-color .14s, box-shadow .14s, transform .14s;
  }
  .ccard:hover {
    border-color: #CDD1D4;
    box-shadow: 0 4px 12px 0 rgba(44,47,50,0.06), 0 2px 4px 0 rgba(44,47,50,0.04);
    transform: translateY(-2px);
  }
  /* Not in the designer's copy, deliberately: unlike a canvas, this page is
     navigated with a keyboard. */
  .ccard:focus-visible { outline: 2px solid ${DEFAULT_THEME}; outline-offset: 2px; }

  /* A fixed slate rather than the store's color: every cover uses this
     placeholder until collections carry an image, and six covers in the accent
     would read as one color block. */
  .cov {
    aspect-ratio: 3/2;
    position: relative;
    display: grid;
    place-items: center;
    overflow: hidden;
    background: linear-gradient(150deg, #3d4a52, #565E64);
  }
  .cov img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
  .cov .glyph { position: relative; z-index: 1; color: rgba(255,255,255,.55); }
  .cov .glyph svg { width: 34px; height: 34px; }
  /* The name sits on the cover, so it needs a scrim under it: without one a
     pale photo leaves white type on white. */
  .cov .scrim {
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(to top, rgba(18,15,13,.8) 0%, rgba(18,15,13,.16) 48%, transparent 70%);
  }
  .cov .cn {
    position: absolute;
    left: 13px;
    right: 13px;
    bottom: 11px;
    z-index: 2;
    color: #FFFFFF;
    font-size: 16.5px;
    font-weight: 600;
    letter-spacing: -0.01em;
    text-shadow: 0 1px 4px rgba(0,0,0,.22);
  }
  .cbody { padding: 12px 13px 13px; flex: 1; display: flex; flex-direction: column; }
  /* Clamped to two lines, so a 250-character description does not set the
     height of every card in the rail. */
  .cd {
    color: #565E64;
    font-size: 12px;
    margin: 0;
    flex: 1;
    line-height: 1.48;
    font-style: italic;
    padding-left: 11px;
    border-left: 2px solid #F3E2DD;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .cmeta { display: flex; align-items: center; gap: 8px; margin-top: 11px; font-size: 11.5px; }
  .cmeta .cshop { font-weight: 600; }
`;

function decodeHtml(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.documentElement.textContent;
}
const previewCardsCount = 4;

/* The stand-in a rail shows between "the section exists" and "its records came
   back": containers are filled from the published html_content first and only
   then hydrated from the API. It draws the rail it stands in for — same
   surface, header and card footprint, from the *_STYLES above — so nothing
   moves when the real tiles replace it. */
const RAIL_SKELETON_MEDIA = {
  brands: `<div style="padding:18px 0 16px"><div class="sk-b" style="width:104px;height:104px;border-radius:50%;margin:0 auto"></div></div>`,
  products: `<div class="sk-b" style="width:100%;aspect-ratio:1/1;border-radius:0"></div>`,
  collections: `<div class="sk-b" style="width:100%;aspect-ratio:3/2;border-radius:0"></div>`,
};

const RAIL_SKELETON_COUNT = { brands: 9, products: 5, collections: 8 };

const SKELETON_RAIL = (kind) => {
  const card =
    `<div class="sk-card sk-card-${kind}">` +
    RAIL_SKELETON_MEDIA[kind] +
    `<div class="sk-cbody sk-cbody-${kind}">` +
    `<div class="sk-b" style="height:12px;width:60%"></div>` +
    `<div class="sk-b" style="height:12px;width:85%"></div>` +
    `</div></div>`;

  return `<section class="sk-sec${kind === 'brands' ? ' sk-alt' : ''}">
  <div class="sk-in">
    <div style="margin-bottom:24px">
      <div class="sk-b" style="height:26px;width:240px;max-width:70%"></div>
      <div class="sk-b" style="height:15px;width:320px;max-width:85%;margin-top:9px"></div>
      <div class="sk-b sk-seeall"></div>
    </div>
    <div class="sk-arrows"><div class="sk-b"></div><div class="sk-b"></div></div>
    <div class="sk-rail">${Array(5).fill(card).join('')}</div>
  </div>
</section>`;
};

const parseStoredVideoUrl = (rawUrl) => {
  const link = String(rawUrl || '').trim();
  return parseVideoEmbed(link.startsWith('#') ? link.slice(1) : link);
};

/* Same player parameters as the designer's buildVideoEmbedUrl, set on a parsed
   URL so a query already on the stored link cannot corrupt `playlist`.
   Browsers only autoplay muted video. `playsinline` is storefront-only: without
   it iOS Safari will not autoplay inline, and the designer canvas never runs
   on a phone. `controls: 0` keeps the player's own bar off; `enablejsapi` lets
   the play/pause button (videoControls.js) drive it. Returns '' for anything
   but an allowed YouTube embed. */
const buildVideoEmbedUrl = (rawUrl) => {
  const embed = parseStoredVideoUrl(rawUrl);
  if (!embed) return '';
  const { id, url } = embed;

  const params = {
    autoplay: 1, mute: 1, loop: 1, playlist: id, showinfo: 0, controls: 0,
    modestbranding: 1, rel: 0, enablejsapi: 1, playsinline: 1, origin: window.location.origin,
    // No captions, no annotations, no keyboard or fullscreen: the section is a backdrop.
    cc_load_policy: 0, iv_load_policy: 3, disablekb: 1, fs: 0,
  };
  Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, String(value)));
  return url.toString();
};
// The API returns its own order; the section's saved order is what customers see.
const inSavedOrder = (items, ids, keyOfItem, keyOfId) => {
  const rank = new Map((ids || []).map((id, i) => [keyOfId(id), i]));
  return (items || []).slice().sort((a, b) => (rank.get(keyOfItem(a)) ?? 1e9) - (rank.get(keyOfItem(b)) ?? 1e9));
};
const brandItemKey = (b) => (b?.type === 'vendor' ? `vendor:${b?.vendor_id}` : `brand:${b?.id}`);
const brandIdKey = (id) => `${id?.brandType || 'brand'}:${id?.id}`;

const StoreBuilder = () => {

  const { pageDetails, storeDetails } = useStore();

  const [editorBrandsData, setEditorBrandsData] = useState([]);
  const [editorProductsData, setEditorProductsData] = useState([]);
  const [collectionList, setCollectionList] = useState([]);
  const fetchProductDetails = async (product_ids) => {
    try {
      if (!product_ids.length) return [];
      const response = await axios.post(API_END_POINT.STORE_ALL_PRODUCTS_OLD, {
        paging: { limit: 100, offset: 0 },
        filter: [{
          "field": "product_ids",
          "options": {
            "operator": "in",
            "values": product_ids
          }
        }],
        sort: [{ fieldName: "created_at", fieldOrder: "DESC" }]
      });
      return response.data.data?.items;
      // setEditorProductsData(response.data.data?.items || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchBrandDetails = async (brand_ids) => {
    // setIsLoading(true);
    try {
      if (!brand_ids.length) return [];

      const brandIds = brand_ids?.filter?.(item => item.brandType === 'brand')?.map(item => item.id);
      const vendorIds = brand_ids?.filter?.(item => item.brandType === 'vendor')?.map(item => item.id);

      const filter = []

      if (brandIds?.length) {
        filter.push({
          "field": "brand_ids",
          "options": {
            "operator": "in",
            "values": brandIds
          }
        })
      }

      if (vendorIds?.length) {
        filter.push({
          "field": "vendor_ids",
          "options": {
            "operator": "in",
            "values": vendorIds
          }
        })
      }

      const response = await axios.post(
        API_END_POINT.STORE_BRANDS_LIST,
        {
          paging: { limit: 100, offset: 0 },
          filter,
          sort: [{ fieldName: "created_at", fieldOrder: "DESC" }]
        }
      );
      return response.data.data?.items;
      // setEditorBrandsData(response.data.data?.items)
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      //   setIsLoading(false);
    }
  };

  /* The pinned Main banner and every added banner go through one builder,
     `buildBannerHtml` — the storefront's copy of the designer's own banner
     markup — so the two cannot drift. It owns the layouts, surface colors,
     alignment, buttons and badges; nothing here branches per layout. */
  /* The store's list endpoint returns only PUBLISHED collections, so a pick the
     partner has since unpublished or deleted drops out rather than leading to a
     404. It has no by-id filter, so the picks are matched here and the
     partner's order wins over the endpoint's `sequence`.

     `product_limit: 0` because the rail shows collections, not their contents.
     `product_count` still comes back, and a collection with nothing published
     is dropped: "Shop collection" has to lead somewhere. */
  const fetchCollectionDetails = async (collection_ids) => {
    try {
      if (!collection_ids.length) return [];

      const response = await axios.post(API_END_POINT.COLLECTION_LIST, {
        paging: { limit: 100, offset: 0 },
        filter: [{ field: 'product_limit', options: { value: 0 } }],
      });

      const byId = new Map(
        (response?.data?.data?.collections || []).map((collection) => [String(collection.id), collection])
      );

      return collection_ids
        .map((id) => byId.get(String(id)))
        .filter((collection) => collection && collection.product_count !== 0);
    } catch (error) {
      console.error('Error fetching collections:', error);
      return [];
    }
  };

  const bannerOptions = () => {
    const font = pageDetails?.page_data?.colorAndApperances?.font;
    return {
      themeColor: pageDetails?.page_data?.colorAndApperances?.colorTheme,
      // Quoted: the stored value is a bare family name, and the ones with a
      // space in them ("General Sans") are invalid CSS unquoted.
      headingFont: font ? `'${font}', sans-serif` : '',
    };
  };

  /* A video banner with no playable URL builds to '' — a black box would be
     all it could show — so its container goes, Main banner included. */
  // No words and no photo: a color, split or image banner would draw an empty band.
  const bannerIsEmpty = (banner) => {
    if (banner?.bannerStyle === 'video') return false;
    const hasText = ['title', 'subtitle', 'eyebrow', 'buttonText', 'secondaryButtonText']
      .some((key) => copyOf(banner?.[key]))
      || (banner?.trustBadges || []).some((badge) => String(badge || '').trim());
    const hasPhoto = Boolean(String(banner?.bannerImage || banner?.mobileBannerImage || '').trim());
    return !hasText && (banner?.bannerStyle === 'color' || !hasPhoto);
  };

  const fillBanner = (container, banner, isMain = false) => {
    const html = buildBannerHtml(banner, {
      ...bannerOptions(),
      videoEmbedUrl: buildVideoEmbedUrl(banner.videoUrl),
      isMain,
    });
    if (!html || bannerIsEmpty(banner)) {
      container.remove();
      return;
    }
    container.innerHTML = html;
    if (banner.bannerStyle === 'video') {
      attachVideoControls(container);
      videoRootsRef.current.add(container);
    }
    attachHeroImageLoader(container, banner.bannerStyle);
  };

  const appendMainBanner = (container) => {
    try {
      if (!container) return;
      const mbValues = pageDetails?.page_data?.mainBanner;
      if (!mbValues) return;
      fillBanner(container, mbValues, true);
    } catch (error) {
      console.error("Main Banner Load Error:", error);
    }
  };

  const appendBanners = async (container, block) => {
    try {
      if (!container) return;
      // A container whose banner is gone from page_data would otherwise keep
      // the canvas markup it was published with.
      if (!block) {
        container.remove();
        return;
      }
      // An added Image banner without a photo is dropped rather than published
      // as an empty band; the pinned Main banner renders without one.
      // No bannerStyle means the builder's default layout, which is Image.
      if ((!block.bannerStyle || block.bannerStyle === "image") && !block.bannerImage) {
        container.remove();
        return;
      }
      fillBanner(container, block);
    } catch (error) {
      console.error("Banner Load Error:", error);
    }
  };

  /* Featured collections — several collections as cards. `buildCollectionsHtml`
     owns the markup, the same builder the designer draws its canvas with, so
     preview and published page cannot drift. It returns '' when there is nothing
     to show, and the empty section is removed rather than left as a frame. One
     collection shown as its products is Collection spotlight, below. */
  const appendFeaturedCollection = async (featuredCollectionContainer, block) => {
    try {
      if (!featuredCollectionContainer) return;

      const records = await fetchCollectionDetails(normalizeCollectionIds(block));
      const html = buildCollectionsHtml(block, records.map(normalizeCollection), bannerOptions());

      if (!html) {
        featuredCollectionContainer.remove();
        return;
      }

      featuredCollectionContainer.innerHTML = html;
      attachScrollButtons(featuredCollectionContainer);
    } catch (error) {
      console.error('Featured Collections Load Error:', error);
    }
  };

  /* Collection spotlight — one collection's first products under its name,
     `buildSpotlightHtml`, the designer's own builder. FETCH_COLLECTION is the
     collection page's own endpoint, so the rail shows exactly what "View
     collection" leads to. An unpublished or deleted collection 404s, so the
     catch removes the section just as an empty result does. */
  const appendCollectionSpotlight = async (container, block) => {
    if (!container) return;
    try {
      const response = await axios.post(API_END_POINT.FETCH_COLLECTION(encodeURIComponent(block.collectionId)), {
        paging: { limit: SPOTLIGHT_PRODUCT_LIMIT, offset: 0 },
        sort: [{ fieldName: 'sequence', fieldOrder: 'ASC' }],
      });
      const data = response?.data?.data || {};
      // The id the link is built from falls back to the one that was asked for.
      const collection = data.collection ? normalizeCollection({ id: block.collectionId, ...data.collection }) : null;
      const html = buildSpotlightHtml(block, collection, (data.products || []).map(normalizeProduct), bannerOptions());

      if (!html) {
        container.remove();
        return;
      }

      container.innerHTML = html;
      attachScrollButtons(container);
    } catch (error) {
      container.remove();
      console.error('Collection Spotlight Load Error:', error);
    }
  };

  /* Featured brands — `buildBrandsHtml`, the designer's own builder; same
     contract as buildCollectionsSection above. */
  const appendFeaturedBrands = async (featuredBrandsContainer, block) => {
    try {
      if (!featuredBrandsContainer) return;

      const brands = inSavedOrder((block?.brandIds ? await fetchBrandDetails(block.brandIds) : []) || [], block.brandIds, brandItemKey, brandIdKey);
      const html = buildBrandsHtml(block, brands, bannerOptions());

      if (!html) {
        featuredBrandsContainer.remove();
        return;
      }

      featuredBrandsContainer.innerHTML = html;
      attachScrollButtons(featuredBrandsContainer);
    } catch (error) {
      console.error('Card Load Error:', error);
    }
  };

  /* Featured products — `buildProductsHtml`, the designer's own builder; same
     contract as buildCollectionsSection above. */
  const appendFeaturedProducts = async (featuredProductsContainer, block) => {
    try {
      if (!featuredProductsContainer) return;

      const records = inSavedOrder((block?.productIds ? await fetchProductDetails(block.productIds) : []) || [], block.productIds, (p) => String(p?.id), String);
      const html = buildProductsHtml(block, records.map(normalizeProduct), bannerOptions());

      if (!html) {
        featuredProductsContainer.remove();
        return;
      }

      featuredProductsContainer.innerHTML = html;
      attachScrollButtons(featuredProductsContainer);
    } catch (error) {
      console.error('Featured Products Load Error:', error);
    }
  };

  /* Link list — the reference's `.sf-linklist`. No icon, and every row opens in
     a new tab like the other storefront buttons, so the `icon` / `newTab` keys
     older links still carry are ignored rather than migrated. */
  const LINK_LIST_ARROW = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg>`;

  const appendLinkList = (container, block) => {
    if (!container) return;

    // A row needs both halves to be a link at all, and a bracketed seed string
    // is a placeholder rather than a label.
    const links = (block?.links || []).filter(
      (link) => copyOf(link?.text) && String(link?.url || '').trim()
    );

    if (!links.length || block?.visible === false) {
      container.remove();
      return;
    }

    const heading = copyOf(block?.title) || LINK_LIST_SEED_HEADING;
    const headingFont = pageDetails?.page_data?.colorAndApperances?.font;
    const headFont = headingFont ? ` font-family: '${headingFont}', sans-serif;` : '';

    const rows = links
      .map(
        (link) =>
          `<li><a href="${escapeHtml(String(link.url).trim())}" target="_blank" rel="noopener noreferrer">${escapeHtml(copyOf(link.text))}${LINK_LIST_ARROW}</a></li>`
      )
      .join('');

    container.innerHTML = `<section class="sf-sec sf-linklist-sec">
  <div class="sf-linklist">
    <h2 style="${headFont}">${escapeHtml(heading)}</h2>
    <ul>${rows}</ul>
  </div>
</section>`;
  };

  /* Special offer — the reference's `.sf-promo` band, matching the designer's
     `promoMarkup.js`. The stored backgroundColor / textColor / buttonColor /
     buttonTextColor are from the four-picker version and are no longer read:
     the band takes the storefront color from the Style tab. */
  const appendPromo = (container, block) => {
    if (!container) return;

    const title = copyOf(block?.title);
    const subtitle = copyOf(block?.subtitle);
    const label = copyOf(block?.buttonText);

    // Nothing written anywhere, so there is no offer to announce — a band of
    // pure color carrying three seed strings is a placeholder, not a section.
    if ((!title && !subtitle && !label) || block?.visible === false) {
      container.remove();
      return;
    }

    const themeColor = pageDetails?.page_data?.colorAndApperances?.colorTheme || DEFAULT_THEME;
    const headingFont = pageDetails?.page_data?.colorAndApperances?.font;
    const headFont = headingFont ? ` font-family: '${headingFont}', sans-serif;` : '';

    /* A button renders when it has a label. `includeButton` is retired: the
       switch is gone from the designer, so a section saved under the old
       defaults carries `includeButton: false` beside a real label. */
    const buttonLink = copyOf(block?.buttonLink);
    const buttonHtml = label
      ? `<a class="pr-btn" href="${buttonLink ? escapeHtml(buttonLink) : '#'}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`
      : '';

    container.innerHTML = `<section class="sf-promo" style="background:${escapeHtml(themeColor)};color:${readableOn(themeColor)}">
  <div class="sf-promo-in">
    <div class="pr-copy">
      <h3 style="${headFont} color: inherit;">${escapeHtml(title || PROMO_SEED_TITLE)}</h3>
      <p style="color: inherit;">${escapeHtml(subtitle || PROMO_SEED_SUBTITLE)}</p>
    </div>
    ${buttonHtml}
  </div>
</section>`;
  };

  /**
   * Draw one Text block: words only, a centered reading column on the page's
   * own background.
   *
   * The stored bannerColor / textColor / includeButton / buttonText / buttonLink
   * are from the old colored-banner version and are no longer read.
   */
  const appendTextBlock = (textBlockContainer, block) => {
    if (!textBlockContainer) return;

    // No body, no section. The whole container goes, gutter and top rhythm
    // included, rather than publishing a lone heading over blank space.
    const body = String(block?.subtitle || '').trim();
    if (!body || block.visible === false) {
      textBlockContainer.remove();
      return;
    }

    const heading = String(block.title || '').trim() || TEXT_BLOCK_SEED_HEADING;
    const headingFont = pageDetails?.page_data?.colorAndApperances?.font;
    const headFont = headingFont ? ` font-family: '${headingFont}', sans-serif;` : '';

    textBlockContainer.innerHTML = `<section class="sf-sec sf-textblock-sec">
  <div class="sf-textblock">
    <h2 style="${headFont}">${escapeHtml(heading)}</h2>
    <p>${escapeHtml(body)}</p>
  </div>
</section>`;
  }

  /* "Show on my storefront", off. Only an explicit false hides — a block saved
     before the switch existed has no `visible` key and stays visible.

     This runs over page_data rather than inside the render loop because a
     container's existence comes from the published html_content, not from
     componentOrder: a section the order has drifted away from still arrives in
     the DOM and would otherwise sit there as an empty band. Removing the
     container rather than hiding it also keeps the copy out of the page source.
     Every branch below already no-ops when its container is gone, so this is the
     only place the flag has to be read. */
  const removeHiddenSections = (root) => {
    Object.entries(HIDEABLE_CONTAINER_PREFIX).forEach(([blockType, prefix]) => {
      (pageDetails?.page_data?.[blockType] || []).forEach((block) => {
        if (block?.visible !== false) return;
        root.querySelector(`#${prefix}-container-${block.id}`)?.remove();
      });
    });
  };

  const htmlRef = useRef(null);
  const videoRootsRef = useRef(new Set());
  const functionInitialAction = () => {
    htmlRef.current.innerHTML = decodeHtml(pageDetails?.html_content)

    htmlRef.current.querySelectorAll("[id^='visit-container-']").forEach(el => el.remove());

    htmlRef.current.querySelectorAll("[id^='featured-brand-container']").forEach(el => {
      el.innerHTML = SKELETON_RAIL('brands');
    });
    htmlRef.current.querySelectorAll("[id^='featured-product-container-']").forEach(el => {
      el.innerHTML = SKELETON_RAIL('products');
    });
    htmlRef.current.querySelectorAll("[id^='featured-collection-container-']").forEach(el => {
      el.innerHTML = SKELETON_RAIL('collections');
    });
    htmlRef.current.querySelectorAll("[id^='collection-spotlight-container-']").forEach(el => {
      el.innerHTML = SKELETON_RAIL('products');
    });

    htmlRef.current.querySelector("#announcement-bar-container")?.remove();
    htmlRef.current.querySelector("#navigation-container")?.remove();

    // Closing and Meet the team buttons published before they opened in a new tab.
    htmlRef.current.querySelectorAll("a.cl-btn, [id^='meet-team-container-'] a.it-btn").forEach(a => {
      a.target = "_blank";
      a.rel = "noopener noreferrer";
    });
  }
  function attachHeroImageLoader(container, bannerStyle) {
    if (!container) return;

    // container may be jQuery-wrapped; take the DOM node.
    const root = container[0] || container; // 👈 add this

    const img = root.querySelector('.hero-img');
    const loader = root.querySelector('.hero-img-loader');
    // Only the legacy template wrapped the photo in <picture>; the banner builder
    // does not, so its height reservation is applied only when one is present.
    // The builder's own <picture> (a mobile image) is not that wrapper.
    const pcimag = root.querySelector('picture:not(.hero-picture)');
    if (!loader) return;

    // Not an image banner: no loader.
    if (bannerStyle !== 'image') {
      loader.style.display = 'none';
      return;
    } else if (pcimag) {
      pcimag.style.height = "400px";
    }

    // Image banner with no <img> found.
    if (!img) {
      loader.style.display = 'none';
      return;
    }

    img.style.opacity = '0';
    img.style.transition = 'opacity 0.3s ease';

    const hideLoader = () => {
      loader.style.display = 'none';
      img.style.opacity = '1';
      if (pcimag) pcimag.style.height = "";
    };
    if (img.complete || img.src.includes("data:image/png;base64")) {
      hideLoader();
    } else {
      img.addEventListener('load', hideLoader, { once: true });
      img.addEventListener('error', hideLoader, { once: true });
    }
  }
  const appendImageWithTextBlock = (imageWithTextBlockContainer, block) => {
    if (!imageWithTextBlockContainer) return;

    const body = copyOf(block?.subtitle);
    const image = String(block?.bannerImage || '').trim();

    // Nothing to show on either side of the split, so the whole container goes
    // rather than publishing an empty frame.
    if ((!body && !image) || block?.visible === false) {
      imageWithTextBlockContainer.remove();
      return;
    }

    const heading = copyOf(block?.title) || IMAGE_WITH_TEXT_SEED_HEADING;
    const headingFont = pageDetails?.page_data?.colorAndApperances?.font;
    const headFont = headingFont ? ` font-family: '${headingFont}', sans-serif;` : '';

    const imageHtml = image
      ? `<div class="it-img"><img src="${escapeHtml(image)}" alt="" loading="lazy"></div>`
      : '';

    /* A button renders when it has a label; `includeButton` is retired — see
       buildPromoHtml. Reading the flag would hide a button the preview shows. */
    const buttonLabel = copyOf(block?.buttonText);
    const buttonLink = copyOf(block?.buttonLink);
    const themeColor = pageDetails?.page_data?.colorAndApperances?.colorTheme;
    const buttonHtml = buttonLabel
      ? `<a class="it-btn" href="${buttonLink ? escapeHtml(buttonLink) : '#'}" target="_blank" rel="noopener noreferrer" style="border-color:${escapeHtml(themeColor ? accentTextOf(themeColor) : '#CDD1D4')};color:${escapeHtml(themeColor ? accentTextOf(themeColor) : '#2C2F32')}">${escapeHtml(buttonLabel)}</a>`
      : '';

    imageWithTextBlockContainer.innerHTML = `<section class="sf-sec sf-imgtext-sec">
  <div class="sf-imgtext${block?.side === 'right' ? ' is-right' : ''}${imageHtml ? '' : ' is-copy-only'}">
    ${imageHtml}
    <div class="it-copy">
      <h2 style="${headFont}">${escapeHtml(heading)}</h2>
      ${body ? `<p>${escapeHtml(body)}</p>` : ''}
      ${buttonHtml}
    </div>
  </div>
</section>`;
  }
  useEffect(() => {
    // Each run re-injects html_content, so the previous run's video banners are
    // gone from the DOM but their window listeners are not.
    const videoRoots = videoRootsRef.current;
    const stopVideoControls = () => {
      videoRoots.forEach((root) => root.videoControlsOff?.());
      videoRoots.clear();
    };
    stopVideoControls();
    if (htmlRef.current) {
      functionInitialAction();
      if (!pageDetails?.page_data) return;
      removeHiddenSections(htmlRef.current);
      const container = htmlRef.current.querySelector("#main-banner-container");
      appendMainBanner(container);
      // The banner is pinned above every added section, whatever position the
      // published markup put it in (the announcement bar and navigation above it
      // are stripped by functionInitialAction).
      container?.parentNode?.prepend(container);
      normalizeComponentOrder(pageDetails?.page_data?.componentOrder).map((block, index) => {
        const { id, type: blockType } = block;
        let divid = '';

        if (blockType === 'textBlocks') {
          divid = `text-block-container-${id}`;
          let blockContainer = htmlRef.current.querySelector(`#${divid}`);
          if (blockContainer) {
            const row = pageDetails.page_data[blockType].find((row) => row.id == id);
            appendTextBlock(blockContainer, row);
          }

        } else if (blockType === 'imageWithTextBlocks') {
          divid = `image-with-text-container-${id}`;
          let blockContainer = htmlRef.current.querySelector(`#${divid}`);
          if (blockContainer) {
            const row = pageDetails.page_data[blockType].find((row) => row.id == id);
            appendImageWithTextBlock(blockContainer, row);
          }
        } else if (blockType === 'featuredCollections') {
          // Store designer and the backend feature API (addToFeatured) both write this id.
          divid = `featured-collection-container-${id}`;
          let blockContainer = htmlRef.current.querySelector(`#${divid}`);
          if (blockContainer) {
            const row = pageDetails.page_data[blockType].find((row) => row.id == id);
            if (!normalizeCollectionIds(row).length) {
              blockContainer.remove();
              return; // Stop further execution
            }
            appendFeaturedCollection(blockContainer, row);
          }
        } else if (blockType === 'collectionSpotlights') {
          divid = `collection-spotlight-container-${id}`;
          let blockContainer = htmlRef.current.querySelector(`#${divid}`);
          if (blockContainer) {
            const row = (pageDetails.page_data[blockType] || []).find((row) => String(row.id) === String(id));
            if (row?.collectionId) {
              appendCollectionSpotlight(blockContainer, row);
            } else {
              blockContainer.remove();
            }
          }
        } else if (blockType === 'featuredProducts') {
          divid = `featured-product-container-${id}`;
          let blockContainer = htmlRef.current.querySelector(`#${divid}`);
          if (blockContainer) {
            const row = pageDetails.page_data[blockType].find((row) => row.id == id);
            if (!row.productIds.length) {
              blockContainer.remove();
              return; // Stop further execution
            }
            appendFeaturedProducts(blockContainer, row);
          }
        } else if (blockType === 'featuredBrands') {
          divid = `featured-brand-container-${id}`;
          let blockContainer = htmlRef.current.querySelector(`#${divid}`);
          if (blockContainer) {
            const row = pageDetails.page_data[blockType].find((row) => row.id == id);
            if (!row?.brandIds?.length) {
              blockContainer.remove();
              return; // Stop further execution
            }
            appendFeaturedBrands(blockContainer, row);
          }
        } else if (blockType === 'banners') {
          divid = `banner-container-${id}`;
          let blockContainer = htmlRef.current.querySelector(`#${divid}`);
          if (blockContainer) {
            const row = pageDetails.page_data[blockType].find((row) => row.id == id);
            appendBanners(blockContainer, row);
          }
        } else if (blockType === 'linkLists') {
          divid = `link-list-container-${id}`;
          let blockContainer = htmlRef.current.querySelector(`#${divid}`);
          if (blockContainer) {
            const row = pageDetails.page_data[blockType]?.find((row) => row.id == id);
            if (!row?.links?.length) {
              blockContainer.remove();
              return;
            }
            appendLinkList(blockContainer, row);
          }
        } else if (blockType === 'promos') {
          divid = `promo-container-${id}`;
          let blockContainer = htmlRef.current.querySelector(`#${divid}`);
          if (blockContainer) {
            const row = pageDetails.page_data[blockType]?.find((row) => row.id == id);
            if (!row) {
              blockContainer.remove();
              return;
            }
            appendPromo(blockContainer, row);
          }
        } else if (CONTENT_CONTAINER_PREFIX[blockType]) {
          const blockContainer = htmlRef.current.querySelector(`#${CONTENT_CONTAINER_PREFIX[blockType]}-container-${id}`);
          const row = (pageDetails.page_data[blockType] || []).find((row) => String(row.id) === String(id));
          if (blockContainer && (!row || !blockContainer.children.length)) blockContainer.remove();
          else if (blockType === 'featuredCategories') {
            applyCategoryTileImages(blockContainer);
            attachScrollButtons(blockContainer);
          }
        }
      });
    }
    return stopVideoControls;
  }, [pageDetails?.page_data, editorProductsData, editorBrandsData]);

  useEffect(() => {
    // placeholder for future use
  }, [])
  useEffect(() => {
    if (!document.querySelector('script[src*="@tailwindcss/browser@4"]')) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4';
      script.type = 'module';
      document.head.appendChild(script);
    }
  }, []);
  /**
   * Set a rail arrow's state from whether there is anything to scroll to.
   *
   * Disabled, never hidden: an arrow that disappears at the end of a rail moves
   * the other arrow under the cursor, so the next click does the opposite of
   * what was meant. Both the attribute and the class are set — the attribute
   * makes a real <button> inert and tells assistive tech, the class covers older
   * rails whose arrows are not buttons.
   */
  const setArrowState = (btn, canScroll) => {
    if (!btn) return;
    btn.classList.remove('scroll-hidden');
    btn.classList.toggle('scroll-disabled', !canScroll);
    if (canScroll) btn.removeAttribute('disabled');
    else btn.setAttribute('disabled', 'disabled');
  };

  const syncArrows = (scroller, leftBtn, rightBtn) => {
    const isScrollable = scroller.scrollWidth > scroller.clientWidth;
    setArrowState(leftBtn, isScrollable && scroller.scrollLeft > 0);
    setArrowState(
      rightBtn,
      isScrollable &&
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1
    );
    const wrapper = leftBtn?.closest('.sf-arrows') || leftBtn?.parentElement;
    if (wrapper && wrapper.classList.contains('sf-arrows')) {
      const section = wrapper.closest('.sf-sec');
      const sh = section ? section.querySelector('.sf-sh') : null;
      if (!isScrollable) {
        wrapper.classList.add('scroll-hidden');
        if (sh) sh.style.paddingRight = '0';
      } else {
        wrapper.classList.remove('scroll-hidden');
        if (sh) sh.style.paddingRight = '';
      }
    }
  };

  // Add this function in StoreBuilder.jsx
  const attachScrollButtons = (container) => {
    if (!container) return;

    const scroller = container.querySelector(".cards-scroller");
    const leftBtn = container.querySelector(".scroll-left");
    const rightBtn = container.querySelector(".scroll-right");

    if (!scroller || !leftBtn || !rightBtn) return;

    if (leftBtn.dataset.initialized === "true") return;
    leftBtn.dataset.initialized = "true";

    leftBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      scroller.scrollBy({ left: -400, behavior: "smooth" });
    });

    rightBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      scroller.scrollBy({ left: 400, behavior: "smooth" });
    });

    const toggleArrows = () => syncArrows(scroller, leftBtn, rightBtn);

    toggleArrows();
    scroller.addEventListener('scroll', toggleArrows);
    // A published page is resized by the reader, not just republished: without
    // this a rail that fits at 1440 keeps its arrows hidden at 390.
    window.addEventListener('resize', toggleArrows);
  };
  /* Home is not inside the themed .sfx root, so the shared cards' hover
     (`var(--o-text)`) had nothing to read. A light colour takes its darker tone. */
  const homeAccent = pageDetails?.page_data?.colorAndApperances?.colorTheme || DEFAULT_THEME;
  const homeColorVars = {
    '--o': homeAccent,
    '--o-text': accentTextOf(homeAccent) !== homeAccent ? accentTextOf(homeAccent) : `color-mix(in oklab, ${homeAccent}, black 8%)`,
    '--o-ink': readableOn(homeAccent),
  };

  const mainBanner = pageDetails?.page_data?.mainBanner;
  const shareImage = mainBanner?.bannerStyle === 'color' ? '' : String(mainBanner?.bannerImage || '').trim();

  return (
    <>
      {/* The hero photo, not the logo: a logo previews as a small bubble. */}
      <ShareMeta
        title={storeDetails?.store_name}
        image={shareImage || storeDetails?.store_logo || pageDetails?.page_data?.logoIdentity?.storeLogo}
      />
      <Helmet>
        <style>{`.scroll-hidden { display: none !important; }`}</style>
        {/* Dimmed and inert, never removed — see setArrowState. .scroll-disabled
            covers published rails whose arrows are not real buttons. */}
        <style>{`.scroll-disabled, .sf-arr[disabled] { opacity: 0.35 !important; cursor: default !important; pointer-events: none !important; }`}</style>
        <style>{TEXT_BLOCK_STYLES}</style>
        <style>{IMAGE_WITH_TEXT_STYLES}</style>
        <style>{BANNER_STYLES}</style>
        <style>{LINK_LIST_STYLES}</style>
        <style>{PROMO_STYLES}</style>
        <style>{RAIL_STYLES}</style>
        <style>{BRANDS_STYLES}</style>
        <style>{PRODUCTS_STYLES}</style>
        <style>{COLLECTIONS_STYLES}</style>
        <style>{CONTENT_SECTION_STYLES}</style>
        <style>{SKELETON_STYLES}</style>
      </Helmet>
      {/* Cards: only the name takes the store colour on hover. "View all" links hover to ShopDot red like the reference (their inline colour needs !important). */}
      <style>{`.storebuilder-reset a.sf-prod:hover, .storebuilder-reset .sf-prod .sf-pbrand:hover { color: #2C2F32; } .storebuilder-reset .sf-prod:hover .sf-pname { color: var(--o-text); } .storebuilder-reset .sf-seeall:hover { color: var(--sd-primary-700, #BB341A) !important; text-decoration: underline; }`}</style>
      {/* Buttons at the reference's size (.sf-btn): 12px 20px, line-height 1.55 → 48px tall, 10px corners. */}
      <style>{`.storebuilder-reset .hero-btn-primary, .storebuilder-reset .hero-btn-secondary, .storebuilder-reset .it-btn, .storebuilder-reset .sf-promo .pr-btn { padding: 12px 20px; line-height: 1.55; border-radius: 10px; }`}</style>
      {pageDetails?.html_content ? <div className="@container storebuilder-reset px-[15px] md:px-[0]" style={homeColorVars} ref={htmlRef} dangerouslySetInnerHTML={{ __html: decodeHtml(pageDetails?.html_content) }} /> : <SkeletonLoader componentOrder={pageDetails?.page_data?.componentOrder} />}

    </>
  );
};

export default StoreBuilder;


/* The home page before `html_content` arrives — the BODY only. The announcement
   strip and the navigation bar come from the header's own HeaderSkeleton,
   already on screen; a second copy would stack two headers and then collapse to
   one. The footer IS here, because <Footer> renders nothing until page_data
   lands.

   Section order comes from componentOrder when there is one — there usually is
   not — so the fallback is the shape of a default storefront. Every height is
   the one its real section settles at, so the swap does not move the page. */
const SK_SECTION = {
  mainBanner: () => <div className="sk-b" style={{ height: 340, borderRadius: 0 }} />,
  banners: () => <div className="sk-b" style={{ height: 330, borderRadius: 0 }} />,

  featuredBrands: () => <SkRail kind="brands" />,
  featuredProducts: () => <SkRail kind="products" />,
  featuredCollections: () => <SkRail kind="collections" />,
  collectionSpotlights: () => <SkRail kind="products" />,

  /* Centered reading column: heading, then three lines of body at the widths a
     centered paragraph actually breaks to. */
  textBlocks: () => (
    <section className="sk-sec" style={{ paddingTop: 0 }}>
      <div className="sk-in">
        <div style={{ maxWidth: 660, margin: '0 auto' }}>
          <div className="sk-b" style={{ height: 27, width: '52%', margin: '0 auto 16px' }} />
          <div className="sk-b" style={{ height: 14, marginBottom: 10 }} />
          <div className="sk-b" style={{ height: 14, marginBottom: 10 }} />
          <div className="sk-b" style={{ height: 14, width: '70%', margin: '0 auto' }} />
        </div>
      </div>
    </section>
  ),

  imageWithTextBlocks: () => (
    <section className="sk-sec" style={{ paddingTop: 0 }}>
      <div className="sk-in">
        <div className="sk-imgtext">
          <div className="sk-b" style={{ aspectRatio: '4 / 3' }} />
          <div>
            <div className="sk-b" style={{ height: 27, width: '65%', marginBottom: 16 }} />
            <div className="sk-b" style={{ height: 14, marginBottom: 10 }} />
            <div className="sk-b" style={{ height: 14, marginBottom: 10 }} />
            <div className="sk-b" style={{ height: 14, width: '80%', marginBottom: 22 }} />
            <div className="sk-b" style={{ height: 40, width: 148, borderRadius: 8 }} />
          </div>
        </div>
      </div>
    </section>
  ),

  /* The store's own color is not known yet, so the band is drawn neutral at the
     right height rather than guessed at in orange. */
  promos: () => (
    <div style={{ padding: '36px 0', background: '#EDEFF0', marginBottom: 32 }}>
      <div className="sk-in" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px' }}>
          <div className="sk-b" style={{ height: 27, width: '60%', marginBottom: 10, backgroundColor: '#E2E5E6' }} />
          <div className="sk-b" style={{ height: 14, width: '44%', backgroundColor: '#E2E5E6' }} />
        </div>
        <div className="sk-b" style={{ height: 43, width: 168, borderRadius: 10, backgroundColor: '#E2E5E6' }} />
      </div>
    </div>
  ),

  linkLists: () => (
    <section className="sk-sec" style={{ paddingTop: 0 }}>
      <div className="sk-in">
        <div style={{ maxWidth: 560 }}>
          <div className="sk-b" style={{ height: 25, width: '45%', marginBottom: 14 }} />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 2px', borderBottom: '1px solid #E5E7E8' }}>
              <div className="sk-b" style={{ height: 15, width: `${58 - i * 6}%` }} />
              <div className="sk-b" style={{ height: 16, width: 16, flex: 'none' }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  ),

  footer: () => (
    <div className="sk-foot">
      <div className="sk-in">
        <div className="sk-foottop">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="sk-b" style={{ width: 40, height: 40, borderRadius: 8, flex: 'none' }} />
              <div className="sk-b" style={{ height: 19, width: 160 }} />
            </div>
            <div className="sk-b" style={{ height: 13, width: 210, marginTop: 14 }} />
            <div className="sk-b" style={{ height: 13, width: 170, marginTop: 8 }} />
            <div className="sk-b" style={{ height: 12, width: 130, marginTop: 16 }} />
          </div>
          {[0, 1].map((col) => (
            <div key={col}>
              <div className="sk-b" style={{ height: 11, width: 68, marginBottom: 16 }} />
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="sk-b" style={{ height: 13, width: `${86 - i * 11}%`, marginBottom: 12 }} />
              ))}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginTop: 32, paddingTop: 18, borderTop: '1px solid #E5E7E8' }}>
          <div className="sk-b" style={{ height: 13, width: 230 }} />
          <div className="sk-b" style={{ height: 13, width: 300, maxWidth: '100%' }} />
        </div>
      </div>
    </div>
  ),
};

/* The same card the injected SKELETON_RAIL draws, in React. The footprints are
   the real ones (200px brand tile, a fifth of the rail per product, 230px 3:2
   cover), because a generic square is what makes a rail jump when it fills. */
const SkRail = ({ kind }) => (
  <section className={`sk-sec${kind === 'brands' ? ' sk-alt' : ''}`}>
    <div className="sk-in">
      <div style={{ marginBottom: 24 }}>
        <div className="sk-b" style={{ height: 26, width: 240, maxWidth: '70%' }} />
        <div className="sk-b" style={{ height: 15, width: 320, maxWidth: '85%', marginTop: 9 }} />
        <div className="sk-b sk-seeall" />
      </div>
      <div className="sk-arrows"><div className="sk-b" /><div className="sk-b" /></div>
      <div className="sk-rail">
        {Array.from({ length: RAIL_SKELETON_COUNT[kind] }, (_, i) => i).map((i) => (
          <div key={i} className={`sk-card sk-card-${kind}`}>
            {kind === 'brands' ? (
              <div style={{ padding: '18px 0 16px' }}>
                <div className="sk-b" style={{ width: 104, height: 104, borderRadius: '50%', margin: '0 auto' }} />
              </div>
            ) : (
              <div className="sk-b" style={{ width: '100%', aspectRatio: kind === 'products' ? '1 / 1' : '3 / 2', borderRadius: 0 }} />
            )}
            <div className={`sk-cbody sk-cbody-${kind}`}>
              <div className="sk-b" style={{ height: 12, width: '60%' }} />
              <div className="sk-b" style={{ height: 12, width: '85%' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* Only the types that can appear on a home page, in the order the designer
   pins them: the banner first, the footer last, everything else in between. */
const SK_DEFAULT_ORDER = [
  'mainBanner',
  'featuredBrands',
  'imageWithTextBlocks',
  'featuredProducts',
  'textBlocks',
  'featuredCollections',
  'promos',
  'linkLists',
];

const SkeletonLoader = ({ componentOrder }) => {
  const ordered = normalizeComponentOrder(componentOrder)
    .map((block) => block?.type)
    .filter((type) => SK_SECTION[type] && type !== 'navigation' && type !== 'announcementBar' && type !== 'footer');

  const sections = ordered.length ? ordered : SK_DEFAULT_ORDER;

  return (
    <div aria-hidden="true">
      {sections.map((type, i) => (
        <React.Fragment key={`${type}-${i}`}>{SK_SECTION[type]()}</React.Fragment>
      ))}
      {SK_SECTION.footer()}
    </div>
  );
};
